<script lang="ts">
	import { get } from 'svelte/store';
	//Formatting
	import { format } from 'svelte-i18n';
	//Design system components
	import { CardGrid, Input, SelectCard, Text, Token } from '@Shelly/shelly-design-system';
	//Helpers
	import { capFirstChar, getErrorOrInfoMessageForMinAndMaxUnit } from 'src/helpers/utilities';
	import { getNumberInputConfig } from 'src/helpers/number_input_helpers';
	import {
		CAMERA_DURATION_MAX,
		CAMERA_DURATION_MIN,
		CAMERA_LOCAL_ACTION_METHODS,
		CAMERA_SET_METHOD,
		getCameraConfigActionTitle,
		getCameraLocalActionCards,
		getCameraLocalActionZones,
		getCameraSetMethod,
		getCameraStateCards,
		getSelectedCameraAction,
		isCameraSetAction,
		type CameraLocalAction,
	} from 'src/helpers/actions/local/devices/camera_local_actions';
	import { getCameraSoundOptions } from 'src/components/Camera/helpers/camera-sounds';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Types
	import type { Device } from 'src/types/Device';
	import type { CameraSoundName } from 'src/components/Camera/types/camera-sounds';
	import type { LocalControlOptions } from 'src/types/requests/schedules/schedules_g2';

	export let controlOption: LocalControlOptions;
	export let controlOptions: LocalControlOptions[] = [];
	export let device: Device;
	export let method = '';

	const zoneOptions = getCameraLocalActionZones(device);
	const actions = getCameraLocalActionCards(zoneOptions.length > 0);
	const soundOptions = getCameraSoundOptions();

	const CAMERA_DURATION_UNIT = get(format)('select_units_seconds', {
		default: 'seconds',
	});

	/**
	 * Where an option keeps its state flags: `camera.set` takes them at the top level, while
	 * `camera.setconfig` and `camerazone.setconfig` nest them under `config`.
	 */
	const flagFields = (
		option: LocalControlOptions,
	): { attr: Record<string, any>; params: Record<string, any> } => {
		if (option?.value === CAMERA_SET_METHOD) {
			return { attr: option.attr, params: option.params };
		}
		// LEGACY_CAMERA_ARM_PRIVACY: arm/privacy stop reaching this branch with the old firmware.
		option.attr.config ??= {};
		option.params.config ??= {};
		return { attr: option.attr.config, params: option.params.config };
	};

	/** Whether the option carries `key` at all, i.e. the user picked a state for it. */
	const isFlagSet = (option: LocalControlOptions, key: string): boolean => {
		const attr = option?.value === CAMERA_SET_METHOD ? option?.attr : option?.attr?.config;
		return !!attr?.[key];
	};

	const isFlagSelected = (option: LocalControlOptions, key: string, value: boolean): boolean => {
		const params = option?.value === CAMERA_SET_METHOD ? option?.params : option?.params?.config;
		return isFlagSet(option, key) && params?.[key] === value;
	};

	const onActionSelect = (action: CameraLocalAction) => {
		selectedAction = action;
		stateOptions = getCameraStateCards(selectedAction);
		method = isCameraSetAction(action)
			? getCameraSetMethod(device)
			: CAMERA_LOCAL_ACTION_METHODS[action];
		controlOption = controlOptions.find((option) => option.value === method);
		if (isCameraSetAction(action)) {
			const { attr } = flagFields(controlOption);
			attr.privacy = false;
			attr.arm = false;
		} else if (action === 'zone') {
			selectedZone = '';
			const { attr, params } = flagFields(controlOption);
			attr.enable = false;
			params.enable = false;
		} else if (action === 'sound') {
			controlOption.attr.sound = false;
			controlOption.params.sound = null;
		}
	};

	const onSoundSelect = (sound: CameraSoundName) => {
		controlOption.attr.sound = true;
		controlOption.params.sound = sound;
		controlOption = controlOption;
	};

	const onConfigStateSelect = (value: boolean) => {
		const flagKey = selectedAction === 'zone' ? 'enable' : selectedAction;
		const { attr, params } = flagFields(controlOption);
		attr[flagKey] = true;
		params[flagKey] = value;
		controlOption = controlOption;
	};

	const onZoneSelect = (zoneId: string) => {
		selectedZone = zoneId;
		controlOption.params.id = Number(zoneId);
		controlOption.attr.config ??= {};
		controlOption.params.config ??= {};
		controlOption.attr.config.enable = false;
		controlOption.params.config.enable = false;
		controlOption = controlOption;
	};

	const setDuration = (event: Event) => {
		const input = event.target as HTMLInputElement;
		const duration = input.value.trim();

		if (duration === '') {
			controlOption.attr.duration = false;
			controlOption.params.duration = null;
			return;
		}

		if (!/^\d+$/.test(duration)) {
			input.value =
				controlOption.params.duration != null ? String(controlOption.params.duration) : '';
			return;
		}

		controlOption.attr.duration = true;
		controlOption.params.duration = Number(duration);
	};

	// Arm and privacy share one method, so the saved flags decide which card is active.
	let selectedAction = getSelectedCameraAction(method);
	if (selectedAction === 'privacy' && isFlagSet(controlOption, 'arm')) {
		selectedAction = 'arm';
	}
	let selectedZone = selectedAction === 'zone' ? String(controlOption.params.id ?? '') : '';
	let stateOptions = getCameraStateCards(selectedAction);

	$: minMaxInfo = getErrorOrInfoMessageForMinAndMaxUnit(
		controlOption.params.duration,
		CAMERA_DURATION_MIN,
		CAMERA_DURATION_MAX,
		CAMERA_DURATION_UNIT,
	);
</script>

<Text element="h2" class="mb-s">
	{capFirstChar($format('label_select_action', { default: 'Select action' }))}
</Text>
<CardGrid sm={200} md={200} xl={200} noPaddingBottom>
	{#each actions as action}
		<div class="d-flex">
			<SelectCard
				cardBgColor={$showNewLayout ? 'color-1' : undefined}
				class="width-full"
				type="horizontal"
				variant="radio"
				title={capFirstChar(action.title)}
				icon={action.icon}
				selected={selectedAction === action.id}
				on:click={() => onActionSelect(action.id)}
			/>
		</div>
	{/each}
</CardGrid>

{#if selectedAction === 'record'}
	<div class="mt-m">
		<Text element="h2" class="mb-s">
			{$format('scene_camera_record_duration', { default: 'Recording duration (optional)' })}
		</Text>
		<Input
			size="m"
			value={String(controlOption.params.duration ?? '')}
			label={$format('local_action_camera_duration_placeholder', {
				default: 'e.g. 30 (in seconds)',
			})}
			{...getNumberInputConfig(true)}
			min={CAMERA_DURATION_MIN}
			max={CAMERA_DURATION_MAX}
			icon="fa fa-timer"
			tabIndex="-1"
			info={minMaxInfo.info}
			error={controlOption.attr.duration && minMaxInfo.error}
			id="input-camera-duration"
			aria-labelledby="input-camera-duration"
			on:input={setDuration}
		/>
	</div>
{:else if selectedAction === 'zone'}
	<div class="mt-m">
		<Text element="h2" class="mb-s">
			{$format('select_zone', { default: 'Select Zone' })}
		</Text>
		<div class="d-flex flex-wrap mb-l gap-s">
			{#each zoneOptions as zone}
				<Token
					id="token-local-camera-zone-{zone.id}"
					title={capFirstChar(zone.title)}
					icon={zone.icon}
					size="m"
					selected={selectedZone === zone.id}
					on:click={() => onZoneSelect(zone.id)}
				/>
			{/each}
		</div>

		{#if selectedZone}
			<Text element="h2" class="mb-s">
				{capFirstChar(
					$format('scene_camera_select_zone_state', {
						values: {
							zone:
								zoneOptions.find((zone) => zone.id === selectedZone)?.title ||
								`Zone ${selectedZone}`,
						},
						default: `Change zone ${selectedZone} to`,
					}),
				)}
			</Text>
			<CardGrid sm={200} md={200} xl={200} noPaddingBottom>
				{#each stateOptions as option}
					<div class="d-flex">
						<SelectCard
							cardBgColor={$showNewLayout ? 'color-1' : undefined}
							class="width-full"
							type="horizontal"
							variant="radio"
							title={capFirstChar(option.title)}
							icon={option.icon}
							selected={isFlagSelected(controlOption, 'enable', option.value)}
							on:click={() => onConfigStateSelect(option.value)}
						/>
					</div>
				{/each}
			</CardGrid>
		{/if}
	</div>
{:else if selectedAction === 'sound'}
	<div class="mt-m">
		<Text element="h2" class="mb-s">
			{$format('scene_camera_select_sound', { default: 'Select sound' })}
		</Text>
		<CardGrid sm={200} md={200} xl={200} noPaddingBottom>
			{#each soundOptions as sound}
				<div class="d-flex">
					<SelectCard
						cardBgColor={$showNewLayout ? 'color-1' : undefined}
						class="width-full"
						type="horizontal"
						variant="radio"
						title={capFirstChar(sound.title)}
						icon={sound.icon}
						selected={controlOption.params.sound === sound.id}
						on:click={() => onSoundSelect(sound.id)}
					/>
				</div>
			{/each}
		</CardGrid>
	</div>
{:else if isCameraSetAction(selectedAction)}
	<div class="mt-m">
		<Text element="h2" class="mb-s">
			{getCameraConfigActionTitle(selectedAction)}
		</Text>
		<CardGrid sm={200} md={200} xl={200} noPaddingBottom>
			{#each stateOptions as option}
				<div class="d-flex">
					<SelectCard
						cardBgColor={$showNewLayout ? 'color-1' : undefined}
						class="width-full"
						type="horizontal"
						variant="radio"
						title={capFirstChar(option.title)}
						icon={option.icon}
						selected={isFlagSelected(controlOption, selectedAction, option.value)}
						on:click={() => onConfigStateSelect(option.value)}
					/>
				</div>
			{/each}
		</CardGrid>
	</div>
{/if}
